import { LEGACY_PRODUCT_FLAGS } from "../config/productFeatureCatalog";
import { resolveZooProductScope } from "../product-access/productScope";
/**
 * Lecture des feature flags du parc courant — POINT DE VÉRITÉ UNIQUE côté client.
 *
 * ⚠️ Les flags vivent à DEUX profondeurs selon l'appelant, et les deux sont légitimes :
 *  - objet d'options « à plat » : `MenuConfig.generateMenu(tier, role, zooOptions)` reçoit
 *    l'objet construit par la Sidebar → `zooOptions.featureFlags`
 *  - tranche Redux : une vue connectée par `mapStateToProps = state => ({ zoo: state.zoo })`
 *    reçoit la tranche complète → `state.zoo.zooOptions.featureFlags`
 *
 * `state.zoo.featureFlags` N'EXISTE PAS : `zoo.reducer.js` range tout le payload de
 * `SET_ZOO_OPTIONS` sous la clé `zooOptions` (vérifié au runtime, cf.
 * `zooFeatureFlags.test.js`). Écrire `this.props.zoo?.featureFlags?.<key>` dans une vue
 * connectée donne donc TOUJOURS `undefined` → gate fail-closed silencieux, même sur un
 * parc où le flag est bien posé. C'est le bug que ce helper existe pour rendre impossible.
 *
 * → Ne jamais réécrire un chemin de flags à la main dans une vue : passer par
 *   `isZooFeatureEnabled(zoo, key)`.
 *
 * Source de vérité en base : `zoo/{zooId}.featureFlags.<key>` (panneau FeatureFlagsPanel
 * de ZooList). Les composants qui lisent le document Firestore directement peuvent passer
 * ce document tel quel : la forme « à plat » est acceptée.
 */

/**
 * Renvoie l'objet des feature flags, quelle que soit la forme reçue.
 * Ne renvoie jamais `undefined` — un parc sans flags se lit comme `{}` (fail-closed).
 *
 * @param {{ featureFlags?: object, zooOptions?: { featureFlags?: object } }} zoo
 *   tranche Redux `state.zoo`, objet `zooOptions` à plat, ou document Firestore `zoo/{zooId}`.
 * @returns {object}
 */
export function getZooFeatureFlags(zoo) {
  if (!zoo || typeof zoo !== "object") return {};
  return zoo.featureFlags || zoo.zooOptions?.featureFlags || {};
}

/**
 * Ce flag est-il explicitement activé sur le parc ?
 * Strictement `=== true` : un champ absent, `undefined` ou truthy-mais-pas-booléen se lit
 * comme désactivé (fail-closed — l'activation d'une beta est un geste admin explicite).
 *
 * @param {object} zoo cf. getZooFeatureFlags
 * @param {string} key nom du flag (ex. "individualFastEval", "training", "technicalModule")
 * @returns {boolean}
 */
export function isZooFeatureEnabled(zoo, key) {
  if (!key) return false;
  // This web-only adapter also accepts the Redux slice; the shared server policy
  // intentionally accepts only the staff-protected document root.
  const scope = resolveZooProductScope(zoo?.zooOptions || zoo);
  const feature = LEGACY_PRODUCT_FLAGS.find((item) => item.legacyFlag === key);
  if (scope.mode !== "legacy" && feature) return scope.can(feature.id);
  return getZooFeatureFlags(zoo)[key] === true;
}
